import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, ThumbsUp, ThumbsDown, ShieldAlert, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, errorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import CitationCard from "@/components/CitationCard";
import AnswerText from "@/components/AnswerText";
import SourceSheet from "@/components/SourceSheet";

function ConfidenceDot({ value, answerable }) {
  const color = !answerable ? "bg-amber-500" : value >= 0.6 ? "bg-emerald-500" : value >= 0.4 ? "bg-amber-500" : "bg-red-500";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {answerable ? `güven ${(value ?? 0).toFixed(2)}` : "kaynak bulunamadı"}
    </span>
  );
}

export default function Playground({ assistant }) {
  const aid = assistant.id;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const endRef = useRef();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const hasDatasets = (assistant.dataset_ids?.length || 0) > 0;

  const send = async (preset) => {
    const q = (typeof preset === "string" ? preset : input).trim();
    if (!q) return;
    if (!hasDatasets) return toast.error("Önce 'Veri Setleri' sekmesinden en az bir veri seti bağlayın");
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const r = await api.playgroundChat(aid, { message: q, chat_id: chatId });
      setChatId(r.chat_id);
      setMessages((m) => [...m, {
        role: "assistant", content: r.answer, answerable: r.answerable,
        confidence: r.confidence, citations: r.citations, message_id: r.message_id,
        animate: true,
      }]);
    } catch (e) {
      toast.error(errorDetail(e, "Cevap alınamadı"), { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  const giveFeedback = async (mid, rating) => {
    try {
      await api.feedback(mid, { rating });
      toast.success("Geri bildirim kaydedildi");
    } catch { toast.error("Kaydedilemedi"); }
  };

  return (
    <div className="flex h-[calc(100vh-320px)] min-h-[420px] flex-col">
      <div className="flex-1 space-y-6 overflow-y-auto pr-1">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-foreground text-background">
              <Sparkles className="h-5 w-5" />
            </div>
            <h3 className="mt-4 text-lg font-bold">Asistanı test edin</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {assistant.config_draft?.behavior?.welcome_message
                || "Dokümanlarınıza dair bir soru sorun. Cevaplar kaynaklarıyla birlikte gelir."}
            </p>
            {assistant.config_draft?.behavior?.suggested_questions?.length > 0 && (
              <div className="mt-5 flex max-w-md flex-wrap justify-center gap-2">
                {assistant.config_draft.behavior.suggested_questions.map((qq, i) => (
                  <button
                    key={i}
                    data-testid={`pg-suggested-q-${i}`}
                    onClick={() => send(qq)}
                    className="rounded-full border border-border bg-background px-3.5 py-1.5 text-sm text-foreground transition-all hover:border-foreground/40 hover:bg-secondary"
                  >
                    {qq}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-msg-${i}`} className="fade-up">
            {m.role === "user" ? (
              <div className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-md bg-foreground px-4 py-2.5 text-sm text-background">
                  {m.content}
                </div>
              </div>
            ) : (
              <div className="max-w-[92%]">
                <div className="mb-1.5 flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary">
                    <Sparkles className="h-3.5 w-3.5" />
                  </div>
                  <ConfidenceDot value={m.confidence} answerable={m.answerable} />
                </div>
                <div className={`rounded-2xl rounded-tl-md border px-4 py-3 text-sm leading-relaxed ${
                  m.answerable ? "border-border bg-background" : "border-amber-200 bg-amber-50"
                }`}>
                  {!m.answerable && (
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-700">
                      <ShieldAlert className="h-3.5 w-3.5" /> Kaynaklarda yeterli bilgi yok
                    </div>
                  )}
                  <AnswerText
                    text={m.content}
                    citations={m.citations}
                    animate={m.animate}
                    onTick={() => endRef.current?.scrollIntoView({ behavior: "smooth" })}
                    onCite={setActiveCite}
                  />
                </div>

                {m.citations?.length > 0 && (
                  <div className="mt-3">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                      <Quote className="h-3.5 w-3.5" /> Kaynaklar
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {m.citations.map((c, idx) => (
                        <CitationCard key={idx} citation={c} index={idx} testId={`citation-${i}-${idx}`} onClick={() => setActiveCite(c)} />
                      ))}
                    </div>
                  </div>
                )}

                {m.message_id && (
                  <div className="mt-2 flex items-center gap-1">
                    <Button data-testid={`fb-up-${i}`} variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground"
                      onClick={() => giveFeedback(m.message_id, "up")}>
                      <ThumbsUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button data-testid={`fb-down-${i}`} variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground"
                      onClick={() => giveFeedback(m.message_id, "down")}>
                      <ThumbsDown className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary">
              <Sparkles className="h-3.5 w-3.5 animate-pulse" />
            </div>
            <span className="animate-pulse">Kaynaklar taranıyor ve cevap hazırlanıyor...</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex items-end gap-2 border-t border-border pt-4">
        <Textarea
          data-testid="playground-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Bir soru sorun..."
          className="max-h-32 min-h-[48px] resize-none rounded-xl"
        />
        <Button data-testid="playground-send-btn" onClick={send} disabled={loading} size="icon" className="h-12 w-12 shrink-0 rounded-xl">
          <Send className="h-4.5 w-4.5" />
        </Button>
      </div>

      <SourceSheet cite={activeCite} onClose={() => setActiveCite(null)} lang="tr" />
    </div>
  );
}
